import React from "react";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteSweepRoundedIcon from "@mui/icons-material/DeleteSweepRounded";
import InsertPhotoOutlinedIcon from "@mui/icons-material/InsertPhotoOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";

const STATUS_CONFIG = {
  pending: { color: "default", label: "Pending" },
  uploading: { color: "info", label: "Uploading" },
  success: { color: "success", label: "Success" },
  error: { color: "error", label: "Error" },
  skipped: { color: "warning", label: "Skipped" },
};

export default function StudentQrUploadPanel({
  bulkUploading,
  description = "Drag and drop QR images or browse from your machine. UIDs are parsed from the file prefix, for example `25L001student_qr.png`.",
  dragActive,
  dropDescription = "Supports multi-file upload. Invalid file names and missing students are reported before data is committed.",
  dropTitle = "Drop student QR images here",
  fileInputRef,
  onClearQueue,
  onDrop,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onFilesSelected,
  onOpenFilePicker,
  onRemoveQueueItem,
  onToggleOverwriteExisting,
  onUploadPending,
  overwriteExisting,
  queue,
  title = "Bulk QR Upload",
}) {
  const summary = queue.reduce(
    (accumulator, item) => {
      accumulator[item.status] += 1;
      return accumulator;
    },
    {
      pending: 0,
      uploading: 0,
      success: 0,
      error: 0,
      skipped: 0,
    },
  );

  const totalItems = queue.length;
  const finishedItems = summary.success + summary.error + summary.skipped;
  const overallProgress = totalItems
    ? Math.round((finishedItems / totalItems) * 100)
    : 0;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {description}
          </Typography>
        </Box>

        <FormControlLabel
          control={
            <Switch
              checked={overwriteExisting}
              onChange={(event) =>
                onToggleOverwriteExisting(event.target.checked)
              }
            />
          }
          label="Overwrite existing QR images"
        />
      </Stack>

      <input
        ref={fileInputRef}
        hidden
        accept="image/*"
        multiple
        type="file"
        onChange={(event) => {
          onFilesSelected(event.target.files);
          event.target.value = "";
        }}
      />

      <Paper
        variant="outlined"
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        sx={{
          mt: 3,
          p: 4,
          borderRadius: 4,
          borderStyle: "dashed",
          borderWidth: 2,
          textAlign: "center",
          bgcolor: dragActive ? "primary.50" : "grey.50",
          borderColor: dragActive ? "primary.main" : "divider",
          transition: "all 0.2s ease",
        }}
      >
        <CloudUploadOutlinedIcon
          color={dragActive ? "primary" : "action"}
          sx={{ fontSize: 46 }}
        />
        <Typography variant="h6" fontWeight={700} sx={{ mt: 2 }}>
          {dropTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {dropDescription}
        </Typography>
        <Button
          variant="contained"
          sx={{ mt: 3, borderRadius: 999 }}
          onClick={onOpenFilePicker}
          startIcon={<InsertPhotoOutlinedIcon />}
        >
          Select Images
        </Button>
      </Paper>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        sx={{ mt: 3 }}
      >
        <Button
          variant="contained"
          startIcon={<PlayArrowRoundedIcon />}
          disabled={bulkUploading || summary.pending === 0}
          onClick={onUploadPending}
          sx={{ borderRadius: 999 }}
        >
          {bulkUploading ? "Uploading..." : `Upload Pending (${summary.pending})`}
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<DeleteSweepRoundedIcon />}
          disabled={bulkUploading || totalItems === 0}
          onClick={onClearQueue}
          sx={{ borderRadius: 999 }}
        >
          Clear Queue
        </Button>
      </Stack>

      {totalItems > 0 && (
        <Box sx={{ mt: 3 }}>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            <Chip label={`Pending ${summary.pending}`} variant="outlined" />
            <Chip label={`Success ${summary.success}`} color="success" />
            <Chip label={`Errors ${summary.error}`} color="error" />
            <Chip label={`Skipped ${summary.skipped}`} color="warning" />
          </Stack>

          <Box sx={{ mb: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1 }}
            >
              <Typography variant="subtitle2">Queue progress</Typography>
              <Typography variant="caption" color="text.secondary">
                {finishedItems} / {totalItems} processed
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={overallProgress}
              sx={{ height: 8, borderRadius: 999 }}
            />
          </Box>

          <List
            dense
            sx={{
              maxHeight: 320,
              overflowY: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
            }}
          >
            {queue.map((item) => {
              const status = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
              const showProgress =
                item.status === "uploading" ||
                (item.status === "success" && item.progress === 100);

              return (
                <ListItem
                  key={item.id}
                  divider
                  secondaryAction={
                    <Tooltip title="Remove from queue">
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => onRemoveQueueItem(item.id)}
                          disabled={bulkUploading && item.status === "uploading"}
                        >
                          <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                  sx={{ alignItems: "flex-start", pr: 7 }}
                >
                  <ListItemText
                    primary={
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        spacing={1}
                        sx={{ mb: 0.5 }}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          {item.fileName}
                        </Typography>
                        <Chip
                          size="small"
                          color={status.color}
                          label={status.label}
                        />
                      </Stack>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          UID: {item.uid || "Not detected"}
                        </Typography>

                        {showProgress && (
                          <LinearProgress
                            variant="determinate"
                            value={item.progress || 0}
                            sx={{ mt: 1, mb: 1, height: 6, borderRadius: 999 }}
                          />
                        )}

                        {item.message && (
                          <Typography
                            variant="caption"
                            display="block"
                            color={
                              item.status === "error"
                                ? "error.main"
                                : "text.secondary"
                            }
                            sx={{ mt: 0.75 }}
                          >
                            {item.message}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}
    </Paper>
  );
}
